import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Check, ChevronLeft, Clock, CalendarDays, ArrowRight } from "lucide-react";
import { api, MXN, formatApiErrorDetail } from "@/lib/api";
import { ThemeToggle } from "@/components/ThemeToggle";
import { Calendar } from "@/components/ui/calendar";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

const STEPS = ["Servicio", "Estilista", "Fecha y hora", "Tus datos", "Confirmar"];

function fmtDate(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export default function Booking() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [services, setServices] = useState([]);
  const [stylists, setStylists] = useState([]);
  const [service, setService] = useState(null);
  const [stylist, setStylist] = useState(null);
  const [date, setDate] = useState(null);
  const [slots, setSlots] = useState([]);
  const [time, setTime] = useState(null);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [form, setForm] = useState({ client_name: "", client_phone: "", client_email: "", notes: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    api.get("/services").then((r) => setServices(r.data)).catch(() => {});
    api.get("/stylists").then((r) => setStylists(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (step === 2 && service && stylist && date) {
      setLoadingSlots(true);
      setTime(null);
      api
        .get("/availability", { params: { stylist_id: stylist.id, date: fmtDate(date), service_id: service.id } })
        .then((r) => setSlots(r.data.slots))
        .catch(() => setSlots([]))
        .finally(() => setLoadingSlots(false));
    }
  }, [step, service, stylist, date]);

  const eligibleStylists = service
    ? stylists.filter((s) => s.specialties.includes(service.name))
    : stylists;

  const canNext = () => {
    if (step === 0) return !!service;
    if (step === 1) return !!stylist;
    if (step === 2) return !!date && !!time;
    if (step === 3) return form.client_name.trim() && form.client_phone.trim();
    return true;
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.post("/appointments", {
        service_id: service.id,
        stylist_id: stylist.id,
        date: fmtDate(date),
        time,
        ...form,
      });
      setDone(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="grid min-h-screen place-items-center bg-background px-5">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="w-full max-w-md border border-border bg-card p-10 text-center"
          data-testid="booking-success"
        >
          <div className="mx-auto mb-6 grid h-16 w-16 place-items-center rounded-full bg-primary text-primary-foreground">
            <Check size={32} strokeWidth={2} />
          </div>
          <h1 className="font-serif-display text-3xl">¡Cita confirmada!</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Te esperamos, {done.client_name.split(" ")[0]}. Recibirás un recordatorio.
          </p>
          <div className="mt-6 space-y-2 border-t border-border pt-6 text-left text-sm">
            <p className="flex justify-between"><span className="text-muted-foreground">Servicio</span><span>{done.service_name}</span></p>
            <p className="flex justify-between"><span className="text-muted-foreground">Estilista</span><span>{done.stylist_name}</span></p>
            <p className="flex justify-between"><span className="text-muted-foreground">Fecha</span><span>{done.date} · {done.time}</span></p>
            <p className="flex justify-between"><span className="text-muted-foreground">Total</span><span className="text-primary">{MXN(done.price)}</span></p>
          </div>
          <Link
            to="/"
            data-testid="booking-home-btn"
            className="mt-8 inline-block w-full bg-primary py-3 text-sm font-medium text-primary-foreground transition-colors hover:bg-gold-dark"
          >
            Volver al inicio
          </Link>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <header className="flex items-center justify-between border-b border-border px-5 py-4 sm:px-8">
        <Link to="/" className="flex items-center gap-2">
          <span className="font-serif-display text-2xl font-semibold">Lupita</span>
          <span className="overline mt-1 text-primary">Estética</span>
        </Link>
        <ThemeToggle />
      </header>

      <div className="mx-auto max-w-3xl px-5 py-10 sm:px-8">
        {/* stepper */}
        <div className="mb-10 flex items-center justify-between">
          {STEPS.map((s, i) => (
            <div key={s} className="flex flex-1 items-center">
              <div className="flex flex-col items-center">
                <div
                  className={`grid h-9 w-9 place-items-center rounded-full border text-sm transition-colors ${
                    i <= step ? "border-primary bg-primary text-primary-foreground" : "border-border text-muted-foreground"
                  }`}
                >
                  {i < step ? <Check size={16} /> : i + 1}
                </div>
                <span className="mt-2 hidden text-xs text-muted-foreground sm:block">{s}</span>
              </div>
              {i < STEPS.length - 1 && (
                <div className={`mx-2 h-px flex-1 ${i < step ? "bg-primary" : "bg-border"}`} />
              )}
            </div>
          ))}
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={step}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.25 }}
          >
            {/* STEP 0 - service */}
            {step === 0 && (
              <div>
                <h2 className="mb-6 font-serif-display text-3xl">Elige tu servicio</h2>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  {services.map((s) => (
                    <button
                      key={s.id}
                      data-testid={`book-service-${s.id}`}
                      onClick={() => { setService(s); setStylist(null); }}
                      className={`flex items-center justify-between border p-4 text-left transition-colors ${
                        service?.id === s.id ? "border-primary bg-primary/5" : "border-border hover:border-primary"
                      }`}
                    >
                      <div>
                        <p className="font-medium">{s.name}</p>
                        <p className="text-xs text-muted-foreground">{s.duration} min · {s.category}</p>
                      </div>
                      <span className="font-serif-display text-lg text-primary">{MXN(s.price)}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* STEP 1 - stylist */}
            {step === 1 && (
              <div>
                <h2 className="mb-6 font-serif-display text-3xl">Elige tu especialista</h2>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  {eligibleStylists.map((st) => (
                    <button
                      key={st.id}
                      data-testid={`book-stylist-${st.id}`}
                      onClick={() => setStylist(st)}
                      className={`flex items-center gap-4 border p-4 text-left transition-colors ${
                        stylist?.id === st.id ? "border-primary bg-primary/5" : "border-border hover:border-primary"
                      }`}
                    >
                      <img src={st.photo} alt={st.name} className="h-14 w-14 rounded-full object-cover" />
                      <div>
                        <p className="font-medium">{st.name}</p>
                        <p className="text-xs text-primary">{st.role}</p>
                      </div>
                    </button>
                  ))}
                  {eligibleStylists.length === 0 && (
                    <p className="text-sm text-muted-foreground">No hay especialistas para este servicio.</p>
                  )}
                </div>
              </div>
            )}

            {/* STEP 2 - date & time */}
            {step === 2 && (
              <div>
                <h2 className="mb-6 font-serif-display text-3xl">Fecha y horario</h2>
                <div className="grid grid-cols-1 gap-8 md:grid-cols-2">
                  <div className="border border-border p-3">
                    <Calendar
                      mode="single"
                      selected={date}
                      onSelect={setDate}
                      disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0)) || d.getDay() === 0}
                      className="mx-auto"
                    />
                  </div>
                  <div>
                    <p className="mb-3 flex items-center gap-2 text-sm text-muted-foreground">
                      <Clock size={15} strokeWidth={1.5} /> Horarios disponibles
                    </p>
                    {!date && <p className="text-sm text-muted-foreground">Selecciona una fecha primero.</p>}
                    {date && loadingSlots && <p className="text-sm text-muted-foreground">Cargando...</p>}
                    {date && !loadingSlots && slots.length === 0 && (
                      <p className="text-sm text-muted-foreground">Sin horarios para este día.</p>
                    )}
                    <div className="grid grid-cols-3 gap-2">
                      {slots.map((s) => (
                        <button
                          key={s}
                          data-testid={`book-slot-${s}`}
                          onClick={() => setTime(s)}
                          className={`border py-2 text-sm transition-colors ${
                            time === s ? "border-primary bg-primary text-primary-foreground" : "border-border hover:border-primary"
                          }`}
                        >
                          {s}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* STEP 3 - details */}
            {step === 3 && (
              <div>
                <h2 className="mb-6 font-serif-display text-3xl">Tus datos</h2>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <Label>Nombre completo *</Label>
                    <Input
                      data-testid="book-input-name"
                      value={form.client_name}
                      onChange={(e) => setForm({ ...form, client_name: e.target.value })}
                      placeholder="Tu nombre"
                    />
                  </div>
                  <div>
                    <Label>Teléfono *</Label>
                    <Input
                      data-testid="book-input-phone"
                      value={form.client_phone}
                      onChange={(e) => setForm({ ...form, client_phone: e.target.value })}
                      placeholder="55 1234 5678"
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <Label>Correo (opcional)</Label>
                    <Input
                      data-testid="book-input-email"
                      value={form.client_email}
                      onChange={(e) => setForm({ ...form, client_email: e.target.value })}
                      placeholder="tu@correo.com"
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <Label>Notas (opcional)</Label>
                    <Textarea
                      data-testid="book-input-notes"
                      value={form.notes}
                      onChange={(e) => setForm({ ...form, notes: e.target.value })}
                      placeholder="Alguna preferencia o alergia"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* STEP 4 - confirm */}
            {step === 4 && (
              <div>
                <h2 className="mb-6 font-serif-display text-3xl">Confirma tu cita</h2>
                <div className="space-y-3 border border-border p-6">
                  {[
                    ["Servicio", service?.name],
                    ["Especialista", stylist?.name],
                    ["Fecha", date ? `${fmtDate(date)} · ${time}` : ""],
                    ["Duración", `${service?.duration} min`],
                    ["Cliente", form.client_name],
                    ["Teléfono", form.client_phone],
                  ].map(([k, v]) => (
                    <p key={k} className="flex justify-between border-b border-border pb-2 text-sm last:border-0">
                      <span className="text-muted-foreground">{k}</span>
                      <span>{v}</span>
                    </p>
                  ))}
                  <p className="flex justify-between pt-2 text-lg">
                    <span className="font-serif-display">Total</span>
                    <span className="font-serif-display text-primary">{MXN(service?.price)}</span>
                  </p>
                  <p className="text-xs text-muted-foreground">El pago se realiza en el local (efectivo o transferencia).</p>
                </div>
              </div>
            )}
          </motion.div>
        </AnimatePresence>

        {/* nav buttons */}
        <div className="mt-10 flex items-center justify-between">
          <button
            data-testid="book-back-btn"
            onClick={() => (step === 0 ? navigate("/") : setStep(step - 1))}
            className="flex items-center gap-1 text-sm text-muted-foreground transition-colors hover:text-primary"
          >
            <ChevronLeft size={16} /> {step === 0 ? "Salir" : "Atrás"}
          </button>
          {step < STEPS.length - 1 ? (
            <button
              data-testid="book-next-btn"
              disabled={!canNext()}
              onClick={() => setStep(step + 1)}
              className="flex items-center gap-2 bg-primary px-7 py-3 text-sm font-medium text-primary-foreground transition-colors hover:bg-gold-dark disabled:opacity-40"
            >
              Continuar <ArrowRight size={16} />
            </button>
          ) : (
            <button
              data-testid="book-confirm-btn"
              disabled={submitting}
              onClick={submit}
              className="flex items-center gap-2 bg-primary px-7 py-3 text-sm font-medium text-primary-foreground transition-colors hover:bg-gold-dark disabled:opacity-40"
            >
              <CalendarDays size={16} /> {submitting ? "Confirmando..." : "Confirmar cita"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
